import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { CapacitorUpdater, type BundleInfo } from '@capgo/capacitor-updater';
import { readonly, shallowRef } from 'vue';
import {checkForUpdate, type AppUpdate, type NativePlatform} from '@/api/updatesApi';
import { compareVersions, isNewerOtaVersion } from '@/utils/otaVersion';
const pendingVersionKey = 'ota-pending-version';
const checkIntervalMs = 5 * 60 * 1000;
const minimumCheckIntervalMs = 60 * 1000;
const availableUpdate = shallowRef<BundleInfo | null>(null);
export const availableAppUpdate = readonly(availableUpdate);

export async function applyAvailableAppUpdate(): Promise<void> {
  const bundle = availableUpdate.value;
  if (!bundle) return;
  // Native set() reloads the WebView itself; a successful call may never resolve.
  await CapacitorUpdater.set({ id: bundle.id });
}

function deferAutomaticActivation(): Promise<void> {
  return CapacitorUpdater.setMultiDelay({ delayConditions: [{ kind: 'kill' }] });
}

export async function getInstalledAppVersion(): Promise<{version: string; source: 'ota' | 'builtin'} | null> {
  if (!Capacitor.isNativePlatform()) return null;

  const nativeVersion = (await App.getInfo()).version;
  try {
    const {bundle} = await CapacitorUpdater.current();
    if (bundle.id !== 'builtin' && bundle.version) {
      return {version: bundle.version, source: 'ota'};
    }
  } catch (error) {
    console.warn('[OTA] unable to read the installed bundle:', error);
  }

  return {version: nativeVersion, source: 'builtin'};
}

export async function downloadUpdate(update: AppUpdate): Promise<BundleInfo> {
  console.info(`[OTA] downloading ${update.version}`);
  const bundle = await CapacitorUpdater.download({
    url: update.url,
    version: update.version,
    checksum: update.checksum,
  });
  if (!bundle.id || bundle.version !== update.version || bundle.status === 'error') {
    throw new Error('Downloaded bundle failed validation');
  }

  return bundle;
}

export async function queueUpdate(bundle: BundleInfo): Promise<void> {
  localStorage.setItem(pendingVersionKey, bundle.version);
  try {
    await deferAutomaticActivation();
    await CapacitorUpdater.next({ id: bundle.id });
    availableUpdate.value = bundle;
    console.info('[OTA] update ready for explicit activation or the next app launch');
  } catch (error) {
    localStorage.removeItem(pendingVersionKey);
    throw error;
  }
}

async function runUpdateCheck(platform: NativePlatform): Promise<void> {
  const update = await checkForUpdate(platform);
  if (!update) return;

  console.info(`[OTA] available version: ${update.version}`);
  if (update.min_native_version !== null) {
    const nativeVersion = (await App.getInfo()).version;
    const compatibility = compareVersions(nativeVersion, update.min_native_version);
    if (compatibility === null) {
      console.warn('[OTA] native version is not semantic; skipping update');
      return;
    }
    if (compatibility < 0) {
      console.info('[OTA] skipped because native version is too old');
      return;
    }
  }

  const { bundle } = await CapacitorUpdater.current();
  const currentVersion = bundle.id === 'builtin' ? null : bundle.version;
  console.info(`[OTA] current bundle: ${currentVersion ?? 'builtin'}`);

  if (!isNewerOtaVersion(update.version, currentVersion)) {
    console.info('[OTA] no newer update available');
    return;
  }

  const queued = await CapacitorUpdater.getNextBundle();
  if (queued && (queued.status === 'pending' || queued.status === 'success') &&
    !isNewerOtaVersion(update.version, queued.version)) {
    availableUpdate.value = queued;
    console.info('[OTA] update is already queued');
    return;
  }

  // Native rollback returns to the old bundle. Avoid reinstalling that failed version.
  if (localStorage.getItem(pendingVersionKey) === update.version) {
    console.warn('[OTA] skipped a bundle that did not become ready after activation');
    return;
  }

  await queueUpdate(await downloadUpdate(update));
}

let initialization: Promise<void> | undefined;

export function initAppUpdater(): Promise<void> {
  initialization ??= initialize();
  return initialization;
}

async function initialize(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  const platform = Capacitor.getPlatform();
  if (platform !== 'ios' && platform !== 'android') return;

  let installedVersion: string | null = null;
  try {
    // Mark the mounted bundle healthy before doing any network work.
    const ready = await CapacitorUpdater.notifyAppReady();
    if (ready.bundle.id !== 'builtin') installedVersion = ready.bundle.version;
    console.info('[OTA] updater initialized');
    if (ready.bundle.id !== 'builtin' &&
      localStorage.getItem(pendingVersionKey) === ready.bundle.version) {
      localStorage.removeItem(pendingVersionKey);
    }
  } catch (error) {
    console.error('[OTA] notifyAppReady failed:', error);
    return;
  }

  if (import.meta.env.VITE_OTA_UPDATES_ENABLED !== 'true') return;

  try {
    // Keep queued bundles from reloading the WebView during a temporary interruption.
    await deferAutomaticActivation();
    const queued = await CapacitorUpdater.getNextBundle();
    if (queued && (queued.status === 'pending' || queued.status === 'success') &&
      isNewerOtaVersion(queued.version, installedVersion)) {
      availableUpdate.value = queued;
    }
  } catch (error) {
    console.warn('[OTA] unable to defer automatic activation:', error);
  }

  let active = true;
  let lastCheckAt: number | undefined;
  let checking: Promise<void> | undefined;
  const check = (): Promise<void> => {
    if (!active) return Promise.resolve();
    if (checking) return checking;
    if (lastCheckAt !== undefined && Date.now() - lastCheckAt < minimumCheckIntervalMs) {
      return Promise.resolve();
    }

    lastCheckAt = Date.now();
    checking = runUpdateCheck(platform)
      .catch(error => console.warn('[OTA] update failed; continuing with the current bundle:', error))
      .finally(() => { checking = undefined; });
    return checking;
  };

  try {
    await App.addListener('appStateChange', ({ isActive }) => {
      active = isActive;
      if (active) void check();
    });
  } catch (error) {
    console.warn('[OTA] unable to listen for app state changes:', error);
  }

  // This service lives for the lifetime of the WebView; initialization is a singleton.
  setInterval(() => { void check(); }, checkIntervalMs);
  await check();
}
